import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';

import {
  BondProceed,
  BondProceedApiItem,
  DeleteBondProceedResult,
  UpsertBondProceedRequest,
} from './bond-proceed.types';
import { apiUrl } from '../../../auth/api-url';

@Injectable({
  providedIn: 'root',
})
export class BondProceedService {
  private readonly endpoint = apiUrl('bondproceed');

  constructor(private readonly http: HttpClient) {}

  getBondProceeds(): Observable<BondProceed[]> {
    return this.http.get<unknown>(this.endpoint).pipe(
      map((response) => this.mapRows(response, (item) => this.normalizeBondProceed(item))),
    );
  }

  createBondProceed(payload: UpsertBondProceedRequest): Observable<void> {
    return this.http.post<void>(this.endpoint, payload);
  }

  updateBondProceed(id: number, payload: UpsertBondProceedRequest): Observable<void> {
    return this.http.put<void>(`${this.endpoint}/${id}`, payload);
  }

  deleteBondProceed(id: number): Observable<DeleteBondProceedResult> {
    return this.http.delete<void>(`${this.endpoint}/${id}`).pipe(
      map((): DeleteBondProceedResult => ({ kind: 'success' })),
      catchError(() => of<DeleteBondProceedResult>({
        kind: 'error',
        message: 'Unable to delete bond proceed at this time. Please try again.',
      })),
    );
  }

  private normalizeBondProceed(item: BondProceedApiItem): BondProceed {
    return {
      id: this.toNumber(item.proceedId),
      name: this.toText(item.name),
      description: this.toText(item.description),
    };
  }

  private mapRows<T>(response: unknown, mapper: (item: BondProceedApiItem) => T): T[] {
    return this.extractRows(response).map(mapper);
  }

  private extractRows(response: unknown): BondProceedApiItem[] {
    if (Array.isArray(response)) {
      return response as BondProceedApiItem[];
    }

    if (response && typeof response === 'object') {
      const record = response as Record<string, unknown>;
      const data = record['data'];
      const items = record['items'];

      if (Array.isArray(data)) {
        return data as BondProceedApiItem[];
      }

      if (Array.isArray(items)) {
        return items as BondProceedApiItem[];
      }
    }

    return [];
  }

  private toText(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private toNumber(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }
}
